import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from './useAuth';
import { apiRequest } from '../utils/apiClient';

// ============================================================================
// Types
// ============================================================================

export interface FavoriteProperty {
  id: string;
  propertyId: string;
  listId?: string;
  notes?: string;
  createdAt: string;
}

export interface FavoriteList {
  id: string;
  name: string;
  description?: string;
  color?: string;
  icon?: string;
  propertyIds: string[];
  createdAt: string;
  updatedAt: string;
}

interface FavoritesContextType {
  // Legacy single-list support (default list)
  favorites: FavoriteProperty[];
  favoriteIds: Set<string>;
  loading: boolean;
  error: string | null;
  isFavorite: (propertyId: string) => boolean;
  toggleFavorite: (propertyId: string, listId?: string) => Promise<boolean>;
  addFavorite: (propertyId: string, listId?: string, notes?: string) => Promise<void>;
  removeFavorite: (propertyId: string, listId?: string) => Promise<void>;
  refreshFavorites: () => Promise<void>;

  // Multiple lists support
  lists: FavoriteList[];
  defaultListId: string;
  createList: (name: string, description?: string, color?: string, icon?: string) => Promise<FavoriteList>;
  updateList: (listId: string, updates: Partial<Pick<FavoriteList, 'name' | 'description' | 'color' | 'icon'>>) => Promise<void>;
  deleteList: (listId: string) => Promise<void>;
  getListById: (listId: string) => FavoriteList | undefined;
  getFavoritesInList: (listId: string) => FavoriteProperty[];
  isPropertyInList: (propertyId: string, listId: string) => boolean;
  moveToList: (propertyId: string, fromListId: string, toListId: string) => Promise<void>;
  copyToList: (propertyId: string, toListId: string) => Promise<void>;
}

const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined);

// ============================================================================
// Storage Keys
// ============================================================================

const LOCAL_STORAGE_KEY = 'safaqat24.favorites';
const LISTS_STORAGE_KEY = 'safaqat24.favorite_lists';
const DEFAULT_LIST_ID = 'default';

function getStorageKey(userId: string): string {
  return `${LOCAL_STORAGE_KEY}.${userId}`;
}

function getListsStorageKey(userId: string): string {
  return `${LISTS_STORAGE_KEY}.${userId}`;
}

// ============================================================================
// Storage Functions
// ============================================================================

function getStoredFavorites(userId: string): FavoriteProperty[] {
  try {
    const cached = localStorage.getItem(getStorageKey(userId));
    if (cached) {
      const parsed = JSON.parse(cached);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    }
  } catch {
    // Ignore cache errors
  }
  return [];
}

function setStoredFavorites(userId: string, favorites: FavoriteProperty[]): void {
  try {
    localStorage.setItem(getStorageKey(userId), JSON.stringify(favorites));
  } catch {
    // Ignore storage errors
  }
}

function createDefaultList(): FavoriteList {
  return {
    id: DEFAULT_LIST_ID,
    name: 'My Favorites',
    description: 'Default favorites list',
    color: '#004D40',
    icon: 'ri-heart-line',
    propertyIds: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

function getStoredLists(userId: string): FavoriteList[] {
  try {
    const cached = localStorage.getItem(getListsStorageKey(userId));
    if (cached) {
      const parsed = JSON.parse(cached);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    }
  } catch {
    // Ignore cache errors
  }
  return [createDefaultList()];
}

function setStoredLists(userId: string, lists: FavoriteList[]): void {
  try {
    localStorage.setItem(getListsStorageKey(userId), JSON.stringify(lists));
  } catch {
    // Ignore storage errors
  }
}

function generateListId(): string {
  return `list_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

// ============================================================================
// Provider
// ============================================================================

export function FavoritesProvider({ children }: { children: ReactNode }) {
  const { user, session } = useAuth();
  const token = session?.token;
  const [favorites, setFavorites] = useState<FavoriteProperty[]>([]);
  // Derived from `favorites` so the two can never drift apart (the old separate
  // state had to be kept in sync manually, which caused saved-property bugs).
  const favoriteIds = useMemo(() => new Set(favorites.map(f => f.propertyId)), [favorites]);
  const [lists, setLists] = useState<FavoriteList[]>([createDefaultList()]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sync favorites from server when user logs in
  const refreshFavorites = useCallback(async () => {
    if (!user?.id) {
      setFavorites([]);
      setLists([createDefaultList()]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Load lists from local storage first
      const storedLists = getStoredLists(user.id);
      setLists(storedLists);

      // Try to fetch favorites and lists from server if authenticated
      if (token) {
        try {
          // Fetch both favorites and lists in parallel
          const [favoritesResponse, listsResponse] = await Promise.all([
            apiRequest<{ favorites: string[] | FavoriteProperty[] }>('/favorites', { token }),
            apiRequest<{ lists: FavoriteList[] }>('/favorites/lists', { token }).catch(() => ({ lists: [] })),
          ]);

          // Handle favorites (could be string[] or FavoriteProperty[])
          const rawFavorites = favoritesResponse.favorites || [];
          const serverFavorites: FavoriteProperty[] = rawFavorites.map((f: string | FavoriteProperty) => {
            if (typeof f === 'string') {
              return { id: f, propertyId: f, createdAt: new Date().toISOString() };
            }
            return f;
          });
          setFavorites(serverFavorites);
          setStoredFavorites(user.id, serverFavorites);

          // Handle lists from server
          const serverLists = listsResponse.lists || [];
          if (serverLists.length > 0) {
            // Merge with default list (ensure default always exists)
            const hasDefaultList = serverLists.some(l => l.id === DEFAULT_LIST_ID);
            const mergedLists = hasDefaultList
              ? serverLists
              : [{ ...createDefaultList(), propertyIds: serverFavorites.map(f => f.propertyId) }, ...serverLists];
            setLists(mergedLists);
            setStoredLists(user.id, mergedLists);
          } else {
            // No server lists, use stored lists from localStorage
            // Get property IDs already assigned to custom lists
            const customLists = storedLists.filter(l => l.id !== DEFAULT_LIST_ID);
            const propertyIdsInCustomLists = new Set(
              customLists.flatMap(l => l.propertyIds)
            );

            // Default list should only contain favorites NOT in any custom list
            const storedDefaultList = storedLists.find(l => l.id === DEFAULT_LIST_ID);
            const defaultList = storedDefaultList || createDefaultList();

            // Only add new favorites to default list if they're not in any custom list
            const newFavoriteIds = serverFavorites
              .map(f => f.propertyId)
              .filter(id => !propertyIdsInCustomLists.has(id));

            // Preserve existing default list items that aren't in custom lists
            const existingDefaultIds = (storedDefaultList?.propertyIds || [])
              .filter(id => !propertyIdsInCustomLists.has(id));

            // Merge: existing items + new items (deduplicated)
            const mergedDefaultIds = [...new Set([...existingDefaultIds, ...newFavoriteIds])];
            defaultList.propertyIds = mergedDefaultIds;
            defaultList.updatedAt = new Date().toISOString();

            setLists([defaultList, ...customLists]);
            setStoredLists(user.id, [defaultList, ...customLists]);
          }
          return;
        } catch (apiError) {
          console.warn('Failed to fetch favorites from server, using local cache:', apiError);
        }
      }

      // Fallback to localStorage if API fails or no token
      const storedFavorites = getStoredFavorites(user.id);
      setFavorites(storedFavorites);
    } catch (err) {
      console.error('Failed to load favorites:', err);
      setError('Failed to load favorites');
    } finally {
      setLoading(false);
    }
  }, [user?.id, token]);

  // Load favorites when user logs in/out
  useEffect(() => {
    refreshFavorites();
  }, [refreshFavorites]);

  const isFavorite = useCallback((propertyId: string): boolean => {
    return favoriteIds.has(propertyId);
  }, [favoriteIds]);

  const isPropertyInList = useCallback((propertyId: string, listId: string): boolean => {
    const list = lists.find(l => l.id === listId);
    return list ? list.propertyIds.includes(propertyId) : false;
  }, [lists]);

  const addFavorite = useCallback(async (propertyId: string, listId?: string, notes?: string) => {
    if (!user?.id) {
      throw new Error('Please sign in to save favorites');
    }
    const uid = user.id;

    const targetListId = listId || DEFAULT_LIST_ID;

    // Check if already favorited
    if (favoriteIds.has(propertyId)) {
      // If already favorited, just add to the list if not already there.
      // Functional update so it composes with same-tick state changes.
      setLists(prev => {
        if (prev.some(l => l.id === targetListId && l.propertyIds.includes(propertyId))) return prev;
        const next = prev.map(list =>
          list.id === targetListId
            ? { ...list, propertyIds: [...list.propertyIds, propertyId], updatedAt: new Date().toISOString() }
            : list
        );
        setStoredLists(uid, next);
        return next;
      });
      return;
    }

    // Optimistic update with temp ID
    const tempFavorite: FavoriteProperty = {
      id: `temp_${Date.now()}`,
      propertyId,
      listId: targetListId,
      notes,
      createdAt: new Date().toISOString(),
    };

    // Functional so saving several properties in quick succession accumulates
    // instead of each call overwriting the previous from a stale closure
    // (fixes: only the latest saved property appearing in a list).
    setFavorites(prev => {
      if (prev.some(f => f.propertyId === propertyId)) return prev;
      const next = [...prev, tempFavorite];
      setStoredFavorites(uid, next);
      return next;
    });

    // Update list (functional — compose on top of same-tick updates so a
    // just-created list isn't overwritten away).
    setLists(prev => {
      const next = prev.map(list =>
        list.id === targetListId && !list.propertyIds.includes(propertyId)
          ? { ...list, propertyIds: [...list.propertyIds, propertyId], updatedAt: new Date().toISOString() }
          : list
      );
      setStoredLists(uid, next);
      return next;
    });

    // Sync with server if authenticated
    if (token) {
      try {
        const response = await apiRequest<{ favorite: FavoriteProperty }>(
          '/favorites',
          {
            method: 'POST',
            body: { propertyId, notes },
            token,
          }
        );
        // Replace the temp favorite with the server one (real id), without
        // dropping any other favorites added meanwhile.
        const serverFavorite = response.favorite;
        setFavorites(prev => {
          const next = prev.map(f =>
            f.propertyId === propertyId ? { ...serverFavorite, listId: targetListId } : f
          );
          setStoredFavorites(uid, next);
          return next;
        });

        // For a CUSTOM list, also persist the list membership server-side — the
        // favorite POST above only creates the favorite record, not the
        // list-item. Without this the server and local list diverge on reload
        // (the "list says 3 but shows 1" bug). The default list needs no item.
        if (targetListId !== DEFAULT_LIST_ID) {
          try {
            await apiRequest(`/favorites/lists/${targetListId}/items/${propertyId}`, {
              method: 'POST',
              token,
            });
          } catch (listErr) {
            console.warn('Failed to sync list membership to server:', listErr);
          }
        }
      } catch (apiError) {
        console.warn('Failed to sync favorite to server:', apiError);
      }
    }
  }, [user?.id, token, favorites, favoriteIds, lists, isPropertyInList]);

  const removeFavorite = useCallback(async (propertyId: string, listId?: string) => {
    if (!user?.id) {
      throw new Error('Please sign in to manage favorites');
    }
    const uid = user.id;

    // If removing from specific list only (not default)
    if (listId && listId !== DEFAULT_LIST_ID) {
      setLists(prev => {
        const next = prev.map(list =>
          list.id === listId
            ? { ...list, propertyIds: list.propertyIds.filter(id => id !== propertyId), updatedAt: new Date().toISOString() }
            : list
        );
        setStoredLists(uid, next);
        return next;
      });
      return;
    }

    // Remove from favorites entirely (functional — don't drop other favorites)
    setFavorites(prev => {
      const next = prev.filter(f => f.propertyId !== propertyId);
      setStoredFavorites(uid, next);
      return next;
    });

    // Remove from all lists
    setLists(prev => {
      const next = prev.map(list => ({
        ...list,
        propertyIds: list.propertyIds.filter(id => id !== propertyId),
        updatedAt: new Date().toISOString(),
      }));
      setStoredLists(uid, next);
      return next;
    });

    // Sync with server if authenticated
    if (token) {
      try {
        await apiRequest(
          `/favorites/${propertyId}`,
          {
            method: 'DELETE',
            token,
          }
        );
      } catch (apiError) {
        console.warn('Failed to sync favorite removal to server:', apiError);
      }
    }
  }, [user?.id, token, favorites, lists]);

  const toggleFavorite = useCallback(async (propertyId: string, listId?: string): Promise<boolean> => {
    if (!user?.id) {
      throw new Error('Please sign in to save favorites');
    }

    const wasInFavorites = favoriteIds.has(propertyId);

    if (wasInFavorites) {
      await removeFavorite(propertyId, listId);
      return false;
    } else {
      await addFavorite(propertyId, listId);
      return true;
    }
  }, [user?.id, favoriteIds, addFavorite, removeFavorite]);

  // ============================================================================
  // List Management
  // ============================================================================

  const createList = useCallback(async (
    name: string,
    description?: string,
    color?: string,
    icon?: string
  ): Promise<FavoriteList> => {
    if (!user?.id) {
      throw new Error('Please sign in to create lists');
    }

    // Optimistic update with temp ID
    const tempList: FavoriteList = {
      id: generateListId(),
      name,
      description,
      color: color || '#004D40',
      icon: icon || 'ri-folder-line',
      propertyIds: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    const updatedLists = [...lists, tempList];
    setLists(updatedLists);
    setStoredLists(user.id, updatedLists);

    // Sync with server if authenticated
    if (token) {
      try {
        const response = await apiRequest<{ list: FavoriteList }>(
          '/favorites/lists',
          {
            method: 'POST',
            body: { name, description, color, icon },
            token,
          }
        );
        // Replace temp list with server list (get real ID)
        const serverList = response.list;
        const finalLists = updatedLists.map(l =>
          l.id === tempList.id ? serverList : l
        );
        setLists(finalLists);
        setStoredLists(user.id, finalLists);
        return serverList;
      } catch (apiError) {
        console.warn('Failed to sync list to server:', apiError);
      }
    }

    return tempList;
  }, [user?.id, token, lists]);

  const updateList = useCallback(async (
    listId: string,
    updates: Partial<Pick<FavoriteList, 'name' | 'description' | 'color' | 'icon'>>
  ): Promise<void> => {
    if (!user?.id) {
      throw new Error('Please sign in to update lists');
    }

    const updatedLists = lists.map(list => {
      if (list.id === listId) {
        return {
          ...list,
          ...updates,
          updatedAt: new Date().toISOString(),
        };
      }
      return list;
    });

    setLists(updatedLists);
    setStoredLists(user.id, updatedLists);

    // Sync with server if authenticated (skip for default list which is local-only)
    if (token && listId !== DEFAULT_LIST_ID) {
      try {
        await apiRequest(
          `/favorites/lists/${listId}`,
          {
            method: 'PUT',
            body: updates,
            token,
          }
        );
      } catch (apiError) {
        console.warn('Failed to sync list update to server:', apiError);
      }
    }
  }, [user?.id, token, lists]);

  const deleteList = useCallback(async (listId: string): Promise<void> => {
    if (!user?.id) {
      throw new Error('Please sign in to delete lists');
    }

    if (listId === DEFAULT_LIST_ID) {
      throw new Error('Cannot delete the default list');
    }

    // Move all properties from this list to default list
    const listToDelete = lists.find(l => l.id === listId);
    let updatedLists: FavoriteList[];

    if (listToDelete && listToDelete.propertyIds.length > 0) {
      const defaultList = lists.find(l => l.id === DEFAULT_LIST_ID);
      if (defaultList) {
        const newDefaultPropertyIds = [
          ...new Set([...defaultList.propertyIds, ...listToDelete.propertyIds])
        ];
        updatedLists = lists
          .filter(l => l.id !== listId)
          .map(l => {
            if (l.id === DEFAULT_LIST_ID) {
              return { ...l, propertyIds: newDefaultPropertyIds, updatedAt: new Date().toISOString() };
            }
            return l;
          });
      } else {
        updatedLists = lists.filter(l => l.id !== listId);
      }
    } else {
      updatedLists = lists.filter(l => l.id !== listId);
    }

    setLists(updatedLists);
    setStoredLists(user.id, updatedLists);

    // Sync with server if authenticated
    if (token) {
      try {
        await apiRequest(
          `/favorites/lists/${listId}`,
          {
            method: 'DELETE',
            token,
          }
        );
      } catch (apiError) {
        console.warn('Failed to sync list deletion to server:', apiError);
      }
    }
  }, [user?.id, token, lists]);

  const getListById = useCallback((listId: string): FavoriteList | undefined => {
    return lists.find(l => l.id === listId);
  }, [lists]);

  const getFavoritesInList = useCallback((listId: string): FavoriteProperty[] => {
    const list = lists.find(l => l.id === listId);
    if (!list) return [];
    // The list's `propertyIds` is the authoritative membership. Return one entry
    // per id, using the real favorite record when we have it and a lightweight
    // stub otherwise — so a property never disappears from its list just because
    // its separate favorite record is missing (server/local divergence). This
    // is what caused "list says 3 but only 1 shows".
    const byId = new Map(favorites.map(f => [f.propertyId, f] as const));
    return list.propertyIds.map(
      (pid) => byId.get(pid) ?? { id: pid, propertyId: pid, createdAt: new Date().toISOString() }
    );
  }, [lists, favorites]);

  const moveToList = useCallback(async (
    propertyId: string,
    fromListId: string,
    toListId: string
  ): Promise<void> => {
    if (!user?.id) {
      throw new Error('Please sign in to manage lists');
    }

    const updatedLists = lists.map(list => {
      if (list.id === fromListId) {
        return {
          ...list,
          propertyIds: list.propertyIds.filter(id => id !== propertyId),
          updatedAt: new Date().toISOString(),
        };
      }
      if (list.id === toListId && !list.propertyIds.includes(propertyId)) {
        return {
          ...list,
          propertyIds: [...list.propertyIds, propertyId],
          updatedAt: new Date().toISOString(),
        };
      }
      return list;
    });

    setLists(updatedLists);
    setStoredLists(user.id, updatedLists);

    // Sync with server if authenticated
    if (token) {
      try {
        // Remove from source list and add to target list
        await Promise.all([
          fromListId !== DEFAULT_LIST_ID
            ? apiRequest(`/favorites/lists/${fromListId}/items/${propertyId}`, { method: 'DELETE', token })
            : Promise.resolve(),
          toListId !== DEFAULT_LIST_ID
            ? apiRequest(`/favorites/lists/${toListId}/items/${propertyId}`, { method: 'POST', token })
            : Promise.resolve(),
        ]);
      } catch (apiError) {
        console.warn('Failed to sync list move to server:', apiError);
      }
    }
  }, [user?.id, token, lists]);

  const copyToList = useCallback(async (
    propertyId: string,
    toListId: string
  ): Promise<void> => {
    if (!user?.id) {
      throw new Error('Please sign in to manage lists');
    }
    const uid = user.id;

    // Functional update so this composes on top of any state set earlier in the
    // same tick (e.g. a list that was just created) instead of clobbering it
    // from a stale `lists` closure. (Fixes: new list vanishing after save.)
    setLists(prev => {
      const next = prev.map(list =>
        list.id === toListId && !list.propertyIds.includes(propertyId)
          ? { ...list, propertyIds: [...list.propertyIds, propertyId], updatedAt: new Date().toISOString() }
          : list
      );
      setStoredLists(uid, next);
      return next;
    });

    // Sync with server if authenticated
    if (token && toListId !== DEFAULT_LIST_ID) {
      try {
        await apiRequest(
          `/favorites/lists/${toListId}/items/${propertyId}`,
          { method: 'POST', token }
        );
      } catch (apiError) {
        console.warn('Failed to sync list copy to server:', apiError);
      }
    }
  }, [user?.id, token, lists]);

  // ============================================================================
  // Context Value
  // ============================================================================

  const value = useMemo<FavoritesContextType>(() => ({
    // Legacy support
    favorites,
    favoriteIds,
    loading,
    error,
    isFavorite,
    toggleFavorite,
    addFavorite,
    removeFavorite,
    refreshFavorites,

    // Lists support
    lists,
    defaultListId: DEFAULT_LIST_ID,
    createList,
    updateList,
    deleteList,
    getListById,
    getFavoritesInList,
    isPropertyInList,
    moveToList,
    copyToList,
  }), [
    favorites, favoriteIds, loading, error, isFavorite, toggleFavorite,
    addFavorite, removeFavorite, refreshFavorites, lists, createList,
    updateList, deleteList, getListById, getFavoritesInList, isPropertyInList,
    moveToList, copyToList
  ]);

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
}

export function useFavorites(): FavoritesContextType {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
}
